const formatVariantPart = (value) => {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) {
    return value.map(formatVariantPart).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, entryValue]) => entryValue != null && entryValue !== "")
      .map(([key, entryValue]) => {
        if (typeof entryValue === "object") {
          return formatVariantPart(entryValue);
        }
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        return `${label}: ${entryValue}`;
      })
      .join(", ");
  }
  return String(value);
};

const buildVariantLabel = (item = {}) => {
  const ram = item.ram ?? item.memory;
  const storage = item.storage ?? item.storage_gb;
  if (ram && storage) return `${formatVariantPart(ram)}/${formatVariantPart(storage)}`;
  if (storage) {
    const storageLabel = formatVariantPart(storage);
    return storageLabel.includes("GB") ? storageLabel : `${storageLabel}GB`;
  }

  const variantList = item.variants ?? item.variant_features ?? [];
  if (Array.isArray(variantList) && variantList.length > 0) {
    const joined = variantList.map(formatVariantPart).filter(Boolean).join(" | ");
    if (joined) return joined;
  }

  const variant = item.variant ?? item.variant_name ?? "";
  return formatVariantPart(variant);
};

const resolveProductName = (item = {}) => {
  const raw = item.name ?? item.product_name ?? item.model_name ?? item.product ?? "";
  if (typeof raw === "object" && raw !== null) {
    return raw.name ?? raw.model_name ?? raw.title ?? "";
  }
  return raw || "";
};

const getIndexValue = (item = {}, fallbackIndex = 0) => {
  const raw =
    item.index ??
    item.indexing ??
    item.order ??
    item.position ??
    item.sort_order;
  return raw != null ? String(raw) : String(fallbackIndex + 1);
};

const sortByIndexValue = (list = []) =>
  [...list].sort((a, b) => Number(a.index) - Number(b.index));

export const normalizeCatalogProduct = (item = {}, index = 0) => {
  const rowId =
    item.id ??
    item.price_list_product_id ??
    item.catalog_product_id ??
    `product-${index}`;

  return {
    id: rowId,
    productId: item.product_id ?? item.product ?? item.product_id_id ?? rowId,
    index: getIndexValue(item, index),
    name: resolveProductName(item),
    variant: buildVariantLabel(item),
    price:
      item.price ??
      item.selling_price ??
      item.catalog_price ??
      item.list_price ??
      item.amount ??
      null,
  };
};

export const normalizeCatalogGroup = (apiGroup = {}, index = 0) => {
  const productsSource =
    apiGroup.products ??
    apiGroup.price_list_products ??
    apiGroup.catalog_products ??
    apiGroup.items ??
    apiGroup.product_list ??
    apiGroup.lines ??
    [];

  const rawIndex =
    apiGroup.index ?? apiGroup.indexing ?? apiGroup.order ?? apiGroup.position;

  return {
    id: apiGroup.id ?? apiGroup.group_id ?? apiGroup.price_list_id,
    index: rawIndex != null ? String(rawIndex) : String(index + 1),
    themeKey: apiGroup.theme_key ?? apiGroup.themeKey ?? apiGroup.theme,
    name:
      apiGroup.name ??
      apiGroup.group_name ??
      apiGroup.price_list_name ??
      apiGroup.title ??
      "",
    currencySymbol:
      apiGroup.currency_symbol ?? apiGroup.currencySymbol ?? apiGroup.currency ?? "€",
    products: sortByIndexValue(productsSource.map(normalizeCatalogProduct)),
  };
};

export { formatVariantPart, resolveProductName };

export const normalizeCatalogGroups = (apiGroups = []) =>
  apiGroups
    .map((group, index) => normalizeCatalogGroup(group, index))
    .sort((a, b) => Number(a.index) - Number(b.index) || Number(a.id) - Number(b.id));
